import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout/Layout";
import QuickOnlineAccountServices from "../../../services/QuickOnlineAccountServices";
import { HollowDotsSpinner } from "react-epic-spinners";
import { SearchIcon } from "@heroicons/react/solid";
import {
  DocumentDownloadIcon,
  EyeIcon,
  PlusIcon,
} from "@heroicons/react/outline";
import Paginate from "../../../utils/Paginate";
import { convert } from "../../../utils/convertDate";
import fire from "../../../utils/Toastr";
import { useListLoading } from "../../../hooks/useListLoading";
import TableSkeleton from "../../../components/TableSkeleton";

export default function QuickOnlineReferralCodes() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const { isSearching, startSearch, endSearch } = useListLoading();
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const fetchRecords = async (page = 1, filters = {}) => {
    const searchTerm = filters.search ?? search;
    const active =
      filters.isActive !== undefined
        ? filters.isActive
        : showInactive
          ? ""
          : "true";
    const isInitialLoad =
      page === 1 && !searchTerm && filters.isActive === undefined;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      startSearch();
    }
    try {
      const response = await QuickOnlineAccountServices.listReferralCodes(
        page,
        pageSize,
        searchTerm,
        active
      );
      if (response && response.data) {
        setRecords(response.data.result || []);
        setPagination({
          pageIndex: response.data.pageIndex,
          pageSize,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
    } catch (error) {
      console.error("Error fetching quick online referral codes:", error);
      fire("error", "Failed to load referral codes");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        endSearch();
      }
    }
  };

  useEffect(() => {
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    fetchRecords(newPage);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRecords(1, { search });
  };

  const handleShowInactiveChange = (e) => {
    const checked = e.target.checked;
    setShowInactive(checked);
    fetchRecords(1, { isActive: checked ? "" : "true" });
  };

  const handleClear = () => {
    setSearch("");
    setShowInactive(false);
    fetchRecords(1, { search: "", isActive: "true" });
  };

  const exportCSV = async () => {
    setExportLoading(true);
    try {
      const resp = await QuickOnlineAccountServices.exportReferralCodes(
        search,
        showInactive ? "" : "true"
      );
      if (resp && resp.data) {
        window.open(resp.data);
      } else {
        fire("error", resp?.message || "Export failed");
      }
    } catch (error) {
      console.error("Error exporting quick online referral codes:", error);
      fire("error", "Failed to export referral codes");
    } finally {
      setExportLoading(false);
    }
  };

  const openView = (record) => {
    navigate(
      `/online/accounts/referral-codes/view?id=${encodeURIComponent(
        record.id
      )}&code=${encodeURIComponent(record.code || "")}`,
      { state: { record, isActiveFilter: showInactive ? "" : "true" } }
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return convert(dateStr);
  };

  return (
    <Layout
      pageTag="account"
      current="/online/accounts/referral-codes"
    >
      <React.Fragment>
        <main>
          <div className="max-w-7xl">
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  Quick Online Referral Code Account Opening
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  Manage referral codes for quick online account opening
                </p>
              </div>
              <div className="mt-4 sm:mt-0 sm:ml-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/online/accounts/referral-codes/create")}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Referral Code
                </button>
                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={exportLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-green-100 border border-green-300 rounded-md"
                >
                  <DocumentDownloadIcon className="w-4 mr-1.5" />
                  {exportLoading ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div className="relative">
                  <p className="text-sm leading-3 text-gray-500 mb-1">Search</p>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(e);
                        }
                      }}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Search by code/name/email"
                    />
                  </div>
                </div>
                <div className="flex items-center h-9">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="showInactive"
                      checked={showInactive}
                      onChange={handleShowInactiveChange}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Show inactive</span>
                  </label>
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleSearchSubmit}
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Search
                  </button>
                  <button
                    onClick={handleClear}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
              <div className="min-w-full py-2 align-middle md:px-6 lg:px-8 bg-white">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <HollowDotsSpinner size={50} color="#4D627E" />
                  </div>
                ) : isSearching ? (
                  <TableSkeleton columns={8} rows={10} />
                ) : records.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No referral codes found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            "Referral Code",
                            "MIS Code",
                            "First Name",
                            "Last Name",
                            "Email",
                            "Description",
                            "Created On",
                            "Actions",
                          ].map((header) => (
                            <th
                              key={header}
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {records.map((record) => (
                          <tr key={record.id}>
                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                              {record.code || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {record.misCode || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {record.firstName || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {record.lastName || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {record.emailAddress || "N/A"}
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                              {record.description || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDateTime(record.createdOn)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <button
                                type="button"
                                onClick={() => openView(record)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                              >
                                <EyeIcon className="w-3 h-3 mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {!loading && records.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200 sm:px-6 bg-white">
                  <Paginate
                    pageIndex={pagination.pageIndex}
                    totalPages={pagination.totalPages}
                    hasPreviousPage={pagination.hasPreviousPage}
                    hasNextPage={pagination.hasNextPage}
                    totalCount={pagination.totalCount}
                    setPageIndex={handlePageChange}
                    previousPage={handlePageChange.bind(
                      null,
                      pagination.pageIndex - 1
                    )}
                    nextPage={handlePageChange.bind(
                      null,
                      pagination.pageIndex + 1
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </React.Fragment>
    </Layout>
  );
}
